// Phase 6 — Track E T-E3. Skeleton primitive (shadcn-derived).
//
// Three flavours per blueprint §13.6 and the plan (per content type):
//   * SkeletonRow  — list-row placeholder
//   * SkeletonCard — card placeholder
//   * SkeletonText — line placeholder

import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const Skeleton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('animate-pulse rounded-md bg-surface-raised', className)}
      role="status"
      aria-busy="true"
      aria-live="polite"
      {...props}
    />
  ),
);
Skeleton.displayName = 'Skeleton';

export function SkeletonRow({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn('flex items-center gap-3 px-4 py-3', className)}
      role="status"
      aria-live="polite"
      aria-label="Loading row"
    >
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}

export function SkeletonCard({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn('rounded-lg border border-border-subtle bg-surface-raised p-5', className)}
      role="status"
      aria-live="polite"
      aria-label="Loading card"
    >
      <Skeleton className="h-5 w-1/3" />
      <div className="mt-3 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  );
}

export function SkeletonText({ className, lines = 3 }: { className?: string; lines?: number }): JSX.Element {
  return (
    <div
      className={cn('space-y-2', className)}
      role="status"
      aria-live="polite"
      aria-label="Loading text"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}
